/**
 * SDK Bridge - Handles communication between the widget app and the parent SDK
 */

type MessageType =
  | 'STRADA_READY'
  | 'STRADA_INIT'
  | 'STRADA_SET_META_FIELDS'
  | 'STRADA_EVENT'
  | 'STRADA_REQUEST_USER_TOKEN'
  | 'STRADA_USER_TOKEN_RESPONSE';

type IframeMessage = {
  type: MessageType;
  payload?: unknown;
};

type InitPayload = {
  organizationId: string;
  assistantId: string;
  greeting?: string;
  metaFields?: Record<string, string | number | boolean>;
};

class SDKBridge {
  private isInIframe: boolean;
  private listeners: Map<MessageType, Array<(payload: unknown) => void>> = new Map();

  constructor() {
    this.isInIframe = window.self !== window.top;
    this.setupMessageListener();
  }

  /**
   * Check if running inside an iframe (embedded mode)
   */
  isEmbedded(): boolean {
    return this.isInIframe;
  }

  /**
   * Send a message to the parent window
   */
  sendToParent(type: MessageType, payload?: unknown): void {
    if (!this.isInIframe || !window.parent) {
      return;
    }

    const message: IframeMessage = { type, payload };
    window.parent.postMessage(message, '*');
  }

  /**
   * Send ready message to parent
   */
  sendReady(): void {
    this.sendToParent('STRADA_READY');
  }

  /**
   * Send event to parent
   */
  sendEvent(eventKey: string, data: unknown): void {
    this.sendToParent('STRADA_EVENT', { eventKey, data });
  }

  /**
   * Request user token from SDK
   * Returns a promise that resolves when the SDK responds with the token
   */
  async requestUserToken(): Promise<string | null> {
    if (!this.isInIframe) {
      return null;
    }

    return new Promise((resolve) => {
      const handleResponse = (payload: unknown) => {
        const token = (payload as { token: string | null }).token;
        this.off('STRADA_USER_TOKEN_RESPONSE', handleResponse);
        resolve(token);
      };

      this.on('STRADA_USER_TOKEN_RESPONSE', handleResponse);
      this.sendToParent('STRADA_REQUEST_USER_TOKEN');

      // Timeout after 5 seconds
      setTimeout(() => {
        this.off('STRADA_USER_TOKEN_RESPONSE', handleResponse);
        resolve(null);
      }, 5000);
    });
  }

  /**
   * Register a listener for a specific message type
   */
  on(type: MessageType, callback: (payload: unknown) => void): void {
    if (!this.listeners.has(type)) {
      this.listeners.set(type, []);
    }
    this.listeners.get(type)!.push(callback);
  }

  /**
   * Remove a listener
   */
  off(type: MessageType, callback: (payload: unknown) => void): void {
    const callbacks = this.listeners.get(type);
    if (callbacks) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  /**
   * Setup message listener for parent communication
   */
  private setupMessageListener(): void {
    if (!this.isInIframe) {
      return;
    }

    window.addEventListener('message', (event) => {
      // In a real implementation, you should validate the origin
      // if (event.origin !== 'https://expected-parent-domain.com') return;

      const message = event.data as IframeMessage;

      if (!message || !message.type) {
        return;
      }

      // Trigger registered listeners
      const callbacks = this.listeners.get(message.type);
      if (callbacks) {
        callbacks.forEach((callback) => callback(message.payload));
      }
    });
  }
}

// Export singleton instance
export const sdkBridge = new SDKBridge();
export type { InitPayload };
